import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Drawer,
  Grid,
  IconButton,
  Link,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Skeleton,
  Snackbar,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Close as CloseIcon,
  ContentCopy as ContentCopyIcon,
  Edit as EditIcon,
  Email as EmailIcon,
  ExpandLess as ExpandLessIcon,
  ExpandMore as ExpandMoreIcon,
  Hotel as HotelIcon,
  HowToReg as HowToRegIcon,
  OpenInNew as OpenInNewIcon,
  Payment as PaymentIcon,
  Person as PersonIcon,
  Phone as PhoneIcon,
  Refresh as RefreshIcon,
  Save as SaveIcon,
  Send as SendIcon,
  WhatsApp as WhatsAppIcon,
} from '@mui/icons-material';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import LinkIcon from '@mui/icons-material/Link';
import ScheduleOutlinedIcon from '@mui/icons-material/ScheduleOutlined';
import moment from 'moment';
import { alpha } from '@mui/material/styles';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { HotelDatePicker, HotelDateTimePicker as DateTimePicker } from '../../shared/components/forms/HotelDatePicker';
import { getWebCheckInInviteDetail, updateWebCheckInInvite } from '../../services/api';
import { detectThemeSync, watchThemeChanges } from '../../utils/themeUtils';
import { formatHotelDate, formatHotelDateTime, fromHotelPickerMomentToIso, toHotelPickerMoment } from '../../utils/dateUtils';
import { getBookingDetailPath, getCustomerDetailPath } from '../../utils/bookingDrawerLinks';
import {
  BOOKING_DRAWER_ACCENT,
  BOOKING_DRAWER_WIDTH_SX,
  getBookingDrawerDividerColor,
  getBookingDrawerFooterSx,
  getBookingDrawerHeaderSx,
  getBookingDrawerOutlinedButtonSx,
  getBookingDrawerPaperSx,
  getBookingDrawerPrimaryButtonSx,
  getBookingDrawerTheme,
} from '../../utils/bookingDrawerLayout';

const EXPIRY_PRESETS = [
  { value: 15, label: '15m' },
  { value: 30, label: '30m' },
  { value: 60, label: '1h' },
  { value: 360, label: '6h' },
  { value: 1440, label: '24h' },
  { value: 2880, label: '48h' },
];

const STATUS_COLORS = {
  pending: { bg: 'rgba(255, 152, 0, 0.14)', color: '#e65100' },
  submitted: { bg: 'rgba(76, 175, 80, 0.14)', color: '#2e7d32' },
  expired: { bg: 'rgba(158, 158, 158, 0.16)', color: '#616161' },
  cancelled: { bg: 'rgba(244, 67, 54, 0.14)', color: '#c62828' },
};

function formatExpiryCountdown(expiresAt) {
  if (!expiresAt) return '';
  const end = moment(expiresAt);
  if (!end.isValid()) return '';
  const remainingMs = end.valueOf() - Date.now();
  if (remainingMs <= 0) return 'Expired';
  const totalSec = Math.floor(remainingMs / 1000);
  const days = Math.floor(totalSec / 86400);
  const hours = Math.floor((totalSec % 86400) / 3600);
  const mins = Math.floor((totalSec % 3600) / 60);
  if (days > 0) return `${days}d ${hours}h left`;
  if (hours > 0) return `${hours}h ${mins}m left`;
  return `${mins}m left`;
}

function SettingRow({ label, hint, control, isDark, drawerTheme, divider = false }) {
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 1.5,
        py: 1,
        borderTop: divider ? `1px solid ${getBookingDrawerDividerColor(isDark)}` : 'none',
      }}
    >
      <Box sx={{ minWidth: 0 }}>
        <Typography
          variant="body2"
          sx={{ fontWeight: 700, color: isDark ? drawerTheme.text : '#1f2937', lineHeight: 1.3 }}
        >
          {label}
        </Typography>
        {hint ? (
          <Typography
            variant="caption"
            sx={{ color: isDark ? drawerTheme.subtleText : '#64748b', fontWeight: 500, display: 'block', mt: 0.2 }}
          >
            {hint}
          </Typography>
        ) : null}
      </Box>
      {control}
    </Box>
  );
}

function formatDateTime(value) {
  if (!value) return '—';
  return formatHotelDateTime(value) || '—';
}

function formatMoney(amount, currency = 'INR') {
  if (amount === null || amount === undefined || amount === '') return '—';
  const num = Number(amount);
  if (Number.isNaN(num)) return `${currency} ${amount}`;
  return `${currency} ${num.toLocaleString('en-IN', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

function InfoRow({ label, value, isDark, drawerTheme }) {
  return (
    <Grid item xs={12} sm={6}>
      <Typography
        variant="caption"
        sx={{ color: isDark ? drawerTheme.subtleText : '#757575', fontWeight: 600, letterSpacing: 0.3 }}
      >
        {label}
      </Typography>
      <Typography
        variant="body2"
        sx={{ color: isDark ? drawerTheme.text : '#1f2937', fontWeight: 600, mt: 0.25, wordBreak: 'break-word' }}
      >
        {value || '—'}
      </Typography>
    </Grid>
  );
}

function SectionPaper({ title, icon, children, isDark, drawerTheme, action = null }) {
  return (
    <Paper
      elevation={0}
      sx={{
        p: 1.5,
        mb: 1.5,
        backgroundColor: isDark ? drawerTheme.surface : '#ffffff',
        border: `1px solid ${isDark ? 'rgba(76, 225, 182, 0.2)' : 'rgba(76, 225, 182, 0.15)'}`,
        borderRadius: '12px',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 1.25 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
          {icon}
          <Typography variant="subtitle2" sx={{ fontWeight: 700, color: isDark ? drawerTheme.text : '#111827' }}>
            {title}
          </Typography>
        </Box>
        {action}
      </Box>
      {children}
    </Paper>
  );
}

function skeletonSx(isDark) {
  return {
    bgcolor: isDark ? alpha('#ffffff', 0.08) : alpha('#0f172a', 0.08),
    '&.MuiSkeleton-wave::after': {
      background: isDark
        ? `linear-gradient(90deg, transparent, ${alpha('#ffffff', 0.12)}, transparent)`
        : undefined,
    },
  };
}

function WebCheckInInviteDrawerSkeleton({ isDark, drawerTheme }) {
  const bone = skeletonSx(isDark);
  const softBorder = getBookingDrawerDividerColor(isDark);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
      <SectionPaper
        title="Booking"
        icon={<HotelIcon sx={{ color: BOOKING_DRAWER_ACCENT, fontSize: 18 }} />}
        isDark={isDark}
        drawerTheme={drawerTheme}
        action={<Skeleton variant="rounded" width={84} height={22} animation="wave" sx={{ ...bone, borderRadius: 999 }} />}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'stretch',
            gap: 0,
            minWidth: 0,
            borderRadius: 2.5,
            overflow: 'hidden',
            border: `1px solid ${softBorder}`,
            bgcolor: isDark ? alpha('#ffffff', 0.03) : alpha('#0f172a', 0.02),
          }}
        >
          <Skeleton
            variant="rectangular"
            animation="wave"
            sx={{
              ...bone,
              width: { xs: 112, sm: 132 },
              minHeight: { xs: 118, sm: 132 },
              flexShrink: 0,
            }}
          />
          <Box sx={{ flex: 1, minWidth: 0, p: 1.5, display: 'flex', flexDirection: 'column', gap: 0.85 }}>
            <Skeleton variant="text" animation="wave" width="58%" height={22} sx={bone} />
            <Skeleton variant="text" animation="wave" width="36%" height={18} sx={bone} />
            <Skeleton variant="rounded" animation="wave" width="72%" height={24} sx={{ ...bone, borderRadius: 999 }} />
            <Skeleton variant="text" animation="wave" width="48%" height={16} sx={bone} />
            <Skeleton variant="rounded" animation="wave" width={88} height={22} sx={{ ...bone, borderRadius: 999, mt: 0.25 }} />
          </Box>
        </Box>
      </SectionPaper>

      <SectionPaper
        title="Web Check-in"
        icon={<HowToRegIcon sx={{ color: BOOKING_DRAWER_ACCENT, fontSize: 18 }} />}
        isDark={isDark}
        drawerTheme={drawerTheme}
        action={<Skeleton variant="rounded" width={72} height={22} animation="wave" sx={{ ...bone, borderRadius: 999 }} />}
      >
        <Box
          sx={{
            p: 1.15,
            mb: 1,
            borderRadius: 2,
            border: `1px solid ${softBorder}`,
            bgcolor: isDark ? alpha('#ffffff', 0.025) : alpha('#0f172a', 0.02),
          }}
        >
          <Stack direction="row" spacing={1} alignItems="center">
            <Skeleton variant="circular" width={28} height={28} animation="wave" sx={bone} />
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Skeleton variant="text" animation="wave" width={72} height={14} sx={bone} />
              <Skeleton variant="text" animation="wave" width="88%" height={18} sx={bone} />
            </Box>
            <Skeleton variant="circular" width={28} height={28} animation="wave" sx={bone} />
            <Skeleton variant="circular" width={28} height={28} animation="wave" sx={bone} />
          </Stack>
        </Box>

        <Box
          sx={{
            px: 1,
            borderRadius: 2,
            border: `1px solid ${softBorder}`,
            bgcolor: isDark ? 'rgba(255,255,255,0.02)' : 'rgba(15,23,42,0.015)',
          }}
        >
          {[0, 1].map((row) => (
            <Box
              key={`setting-skel-${row}`}
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 1.5,
                py: 1,
                borderTop: row ? `1px solid ${softBorder}` : 'none',
              }}
            >
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Skeleton variant="text" animation="wave" width={88} height={16} sx={bone} />
                <Skeleton variant="text" animation="wave" width={132} height={14} sx={bone} />
              </Box>
              <Skeleton variant="rounded" animation="wave" width={40} height={22} sx={{ ...bone, borderRadius: 999 }} />
            </Box>
          ))}
        </Box>

        <Box
          sx={{
            mt: 1,
            px: 1.15,
            py: 1,
            borderRadius: 2,
            border: `1px solid ${softBorder}`,
            display: 'flex',
            alignItems: 'center',
            gap: 1,
          }}
        >
          <Skeleton variant="circular" width={22} height={22} animation="wave" sx={bone} />
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Skeleton variant="text" animation="wave" width="62%" height={16} sx={bone} />
          </Box>
          <Skeleton variant="rounded" animation="wave" width={64} height={22} sx={{ ...bone, borderRadius: 999 }} />
        </Box>
      </SectionPaper>

      <SectionPaper
        title="Customer"
        icon={<PersonIcon sx={{ color: BOOKING_DRAWER_ACCENT, fontSize: 18 }} />}
        isDark={isDark}
        drawerTheme={drawerTheme}
        action={<Skeleton variant="rounded" width={96} height={22} animation="wave" sx={{ ...bone, borderRadius: 999 }} />}
      >
        <Skeleton variant="text" animation="wave" width="42%" height={22} sx={bone} />
        <Skeleton variant="text" animation="wave" width="78%" height={18} sx={bone} />
      </SectionPaper>

      <SectionPaper
        title="Payment"
        icon={<PaymentIcon sx={{ color: BOOKING_DRAWER_ACCENT, fontSize: 18 }} />}
        isDark={isDark}
        drawerTheme={drawerTheme}
        action={<Skeleton variant="rounded" width={88} height={22} animation="wave" sx={{ ...bone, borderRadius: 999 }} />}
      >
        <Grid container spacing={1.5}>
          <Grid item xs={6}>
            <Skeleton variant="text" animation="wave" width={88} height={14} sx={bone} />
            <Skeleton variant="text" animation="wave" width="72%" height={24} sx={bone} />
          </Grid>
          <Grid item xs={6}>
            <Skeleton variant="text" animation="wave" width={72} height={14} sx={bone} />
            <Skeleton variant="text" animation="wave" width="68%" height={24} sx={bone} />
          </Grid>
        </Grid>
      </SectionPaper>
    </Box>
  );
}

function SummaryLine({ children, isDark, drawerTheme }) {
  return (
    <Typography
      variant="body2"
      sx={{
        color: isDark ? drawerTheme.text : '#1f2937',
        fontWeight: 600,
        lineHeight: 1.45,
        wordBreak: 'break-word',
      }}
    >
      {children}
    </Typography>
  );
}

function MetaLine({ children, isDark, drawerTheme }) {
  return (
    <Typography
      variant="caption"
      sx={{
        display: 'block',
        mt: 0.35,
        color: isDark ? drawerTheme.subtleText : '#64748b',
        fontWeight: 600,
        lineHeight: 1.4,
      }}
    >
      {children}
    </Typography>
  );
}

function ViewMoreButton({ open, onClick, label = 'View more' }) {
  return (
    <Button
      size="small"
      onClick={onClick}
      endIcon={open ? <ExpandLessIcon sx={{ fontSize: 16 }} /> : <ExpandMoreIcon sx={{ fontSize: 16 }} />}
      sx={{
        textTransform: 'none',
        fontWeight: 700,
        fontSize: '0.78rem',
        color: BOOKING_DRAWER_ACCENT,
        px: 0.5,
        minWidth: 0,
        '&:hover': { backgroundColor: 'rgba(76, 225, 182, 0.08)' },
      }}
    >
      {open ? 'Hide' : label}
    </Button>
  );
}

function EntityLinkButton({ label, onClick }) {
  return (
    <Button
      size="small"
      endIcon={<OpenInNewIcon sx={{ fontSize: 14 }} />}
      onClick={onClick}
      sx={{
        textTransform: 'none',
        fontWeight: 700,
        fontSize: '0.78rem',
        color: BOOKING_DRAWER_ACCENT,
        px: 0.5,
        minWidth: 0,
        '&:hover': { backgroundColor: 'rgba(76, 225, 182, 0.08)' },
      }}
    >
      {label}
    </Button>
  );
}

function buildEditState(invite) {
  return {
    guest_name: invite?.guest_name || '',
    guest_email: invite?.guest_email || '',
    guest_phone: invite?.guest_phone || '',
    guest_country_code: invite?.guest_country_code || '+91',
    check_in_date: invite?.check_in_date || '',
    check_out_date: invite?.check_out_date || '',
    expires_at: invite?.expires_at ? toHotelPickerMoment(invite.expires_at) : null,
  };
}

function formatPhoneWithCountryCode(phone, countryCode) {
  const national = String(phone || '').trim();
  if (!national) return '';
  const dial = String(countryCode || '+91').trim() || '+91';
  return `${dial} ${national}`;
}

const WebCheckInInviteDrawer = ({ open, onClose, inviteId, onUpdated }) => {
  const navigate = useNavigate();
  const [currentTheme, setCurrentTheme] = useState(() => detectThemeSync());
  const isDark = currentTheme === 'dark';
  const drawerTheme = getBookingDrawerTheme(isDark);
  const [invite, setInvite] = useState(null);
  const [error, setError] = useState(null);
  const [copyOpen, setCopyOpen] = useState(false);
  const [snackMessage, setSnackMessage] = useState('Invite link copied');
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState(() => buildEditState(null));
  const [expanded, setExpanded] = useState({ guest: false, booking: false, payment: false });
  const [cancelConfirmOpen, setCancelConfirmOpen] = useState(false);
  const [resendMenuAnchor, setResendMenuAnchor] = useState(null);
  const [resending, setResending] = useState('');

  useEffect(() => watchThemeChanges(setCurrentTheme), []);

  useEffect(() => {
    if (!open || !inviteId) {
      setInvite(null);
      setError(null);
      setEditing(false);
      setEditForm(buildEditState(null));
      setExpanded({ guest: false, booking: false, payment: false });
      setCancelConfirmOpen(false);
      setResendMenuAnchor(null);
      setResending('');
      return undefined;
    }

    let cancelled = false;
    const load = async () => {
      setError(null);
      setInvite(null);
      setEditing(false);
      try {
        const data = await getWebCheckInInviteDetail(inviteId);
        if (cancelled) return;
        const next = data?.invite || null;
        setInvite(next);
        setEditForm(buildEditState(next));
      } catch (err) {
        if (!cancelled) setError(err.message || 'Failed to load web check-in invite');
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [open, inviteId]);

  const statusStyle = useMemo(() => {
    const key = String(invite?.status || '').toLowerCase();
    return STATUS_COLORS[key] || STATUS_COLORS.expired;
  }, [invite?.status]);

  const guestSubmission = invite?.guest_submission_payload?.guest || {};
  const canEdit = Boolean(invite?.can_edit);
  const isActive = Boolean(invite?.is_active);
  const inviteStatus = String(invite?.status || '').toLowerCase();
  const canCancel = Boolean(
    canEdit
    && inviteStatus
    && inviteStatus !== 'cancelled'
    && inviteStatus !== 'submitted'
  );
  const canResend = Boolean(
    canEdit
    && inviteStatus
    && inviteStatus !== 'cancelled'
    && inviteStatus !== 'submitted'
    && invite?.public_link
  );
  const hasGuestEmail = Boolean(String(invite?.guest_email || '').trim());
  const hasGuestPhone = Boolean(String(invite?.guest_phone || '').trim());
  const submittedPayments = useMemo(
    () => (Array.isArray(invite?.submitted_payments) ? invite.submitted_payments : []),
    [invite?.submitted_payments]
  );
  const paymentSummary = useMemo(() => {
    const currency = invite?.payment_currency || 'INR';
    const bookingTotal = Number(invite?.booking_total_amount);
    const requiredAmount = Number(invite?.payment_amount);
    const successful = submittedPayments.filter(
      (payment) => String(payment?.status || '').toLowerCase() === 'success'
    );
    const collected = successful.reduce((sum, payment) => sum + (Number(payment.amount) || 0), 0);
    const remaining = Number.isFinite(bookingTotal)
      ? Math.max(0, bookingTotal - collected)
      : null;
    return {
      currency,
      bookingTotal: Number.isFinite(bookingTotal) ? bookingTotal : null,
      requiredAmount: Number.isFinite(requiredAmount) ? requiredAmount : null,
      collected,
      remaining,
      paymentCount: submittedPayments.length,
      successfulCount: successful.length,
    };
  }, [invite?.booking_total_amount, invite?.payment_amount, invite?.payment_currency, submittedPayments]);
  const stayRoom = invite?.submitted_stay_room || null;
  const displayRoom = useMemo(() => {
    if (stayRoom?.number) {
      return {
        number: stayRoom.number,
        room_type_name: stayRoom.room_type_name || '',
        image_url: stayRoom.image_url || invite?.room?.image_url || '',
      };
    }
    return {
      number: invite?.room?.number || '',
      room_type_name: invite?.room?.room_type_name || '',
      image_url: invite?.room?.image_url || '',
    };
  }, [invite?.room, stayRoom]);
  const roomChangeLabel = useMemo(() => {
    if (!stayRoom) return '';
    if (stayRoom.is_upgrade) {
      const fromType = stayRoom.from_room_type_name || stayRoom.invite_room_type_name;
      const fromRoom = stayRoom.from_number || stayRoom.invite_number;
      if (fromType && fromRoom) return `Upgraded from Room ${fromRoom} · ${fromType}`;
      if (fromType) return `Upgraded from ${fromType}`;
      if (fromRoom) return `Upgraded from Room ${fromRoom}`;
      return 'Room upgraded';
    }
    if (stayRoom.is_changed) {
      const fromRoom = stayRoom.from_number || stayRoom.invite_number;
      const fromType = stayRoom.from_room_type_name || stayRoom.invite_room_type_name;
      if (fromRoom && fromType) return `Changed from Room ${fromRoom} · ${fromType}`;
      if (fromRoom) return `Changed from Room ${fromRoom}`;
      return 'Room changed';
    }
    return '';
  }, [stayRoom]);
  const outlinedBtnSx = getBookingDrawerOutlinedButtonSx(isDark);
  const primaryBtnSx = getBookingDrawerPrimaryButtonSx(isDark);

  const applyInviteUpdate = (nextInvite, message) => {
    setInvite(nextInvite);
    setEditForm(buildEditState(nextInvite));
    setEditing(false);
    setCancelConfirmOpen(false);
    if (message) {
      setSnackMessage(message);
      setCopyOpen(true);
    }
    onUpdated?.(nextInvite);
  };

  const runUpdate = async (payload, successMessage) => {
    if (!inviteId) return;
    setSaving(true);
    setError(null);
    try {
      const data = await updateWebCheckInInvite(inviteId, payload);
      applyInviteUpdate(data?.invite || null, successMessage);
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to update invite');
    } finally {
      setSaving(false);
    }
  };

  const handleResend = async (channel) => {
    if (!inviteId || !canResend) return;
    setResendMenuAnchor(null);
    setResending(channel);
    setError(null);
    const sendEmail = channel === 'email' || channel === 'both';
    const sendWhatsApp = channel === 'whatsapp' || channel === 'both';
    try {
      const data = await updateWebCheckInInvite(inviteId, {
        action: 'resend',
        channel: channel === 'both' ? 'both' : channel,
        send_email: sendEmail,
        send_whatsapp: sendWhatsApp,
      });
      if (sendWhatsApp) {
        if (data?.whatsapp_web_fallback) {
          const message = String(data?.whatsapp_message || '').trim();
          const dialDigits = String(
            data?.whatsapp_country_code
            || invite?.guest_country_code
            || '+91'
          ).replace(/\D/g, '');
          const nationalDigits = String(invite?.guest_phone || '').replace(/\D/g, '');
          const toPhone = String(data?.whatsapp_to || '').replace(/\D/g, '')
            || `${dialDigits}${nationalDigits}`;
          if (!message || !toPhone) {
            throw new Error('Unable to open WhatsApp Web with the invite message.');
          }
          window.open(
            `https://wa.me/${toPhone}?text=${encodeURIComponent(message)}`,
            '_blank',
            'noopener,noreferrer'
          );
          applyInviteUpdate(
            data?.invite || invite,
            data?.message
              || (sendEmail
                ? 'Email resent · opened WhatsApp Web'
                : 'Opened WhatsApp Web with invite message')
          );
          return;
        }
        if (!data?.whatsapp_sent) {
          throw new Error(data?.whatsapp_user_message || data?.error || 'WhatsApp message was not sent.');
        }
      }
      applyInviteUpdate(
        data?.invite || invite,
        data?.message
          || (channel === 'both'
            ? 'Email and WhatsApp invite resent'
            : channel === 'whatsapp'
              ? 'WhatsApp invite resent'
              : 'Email invite resent')
      );
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to resend invite');
    } finally {
      setResending('');
    }
  };

  const handleCopyLink = async () => {
    if (!invite?.public_link) return;
    try {
      await navigator.clipboard.writeText(invite.public_link);
      setSnackMessage('Invite link copied');
      setCopyOpen(true);
    } catch {
      setError('Unable to copy invite link');
    }
  };

  const handleToggleActive = async (checked) => {
    if (!canEdit || saving) return;
    if (checked) {
      await runUpdate({ action: 'activate' }, 'Invite link activated');
      return;
    }
    await runUpdate({ action: 'deactivate' }, 'Invite link deactivated');
  };

  const handleCancelRequest = async () => {
    if (!canCancel || saving) return;
    await runUpdate({ action: 'cancel' }, 'Web check-in request cancelled');
  };

  const handleSaveEdits = async () => {
    if (!canEdit || saving) return;
    if (!editForm.guest_name.trim()) {
      setError('Guest name is required.');
      return;
    }
    if (!editForm.expires_at || !moment(editForm.expires_at).isValid()) {
      setError('Valid expiry is required.');
      return;
    }
    const expiresIso = fromHotelPickerMomentToIso(editForm.expires_at);
    if (!expiresIso) {
      setError('Valid expiry is required.');
      return;
    }
    await runUpdate(
      {
        guest_name: editForm.guest_name.trim(),
        guest_email: editForm.guest_email.trim(),
        guest_phone: editForm.guest_phone.trim(),
        guest_country_code: editForm.guest_country_code || '+91',
        check_in_date: editForm.check_in_date,
        check_out_date: editForm.check_out_date,
        expires_at: expiresIso,
      },
      'Invite updated'
    );
  };

  const handleSaveExpiryOnly = async () => {
    if (!canEdit || saving) return;
    if (!editForm.expires_at || !moment(editForm.expires_at).isValid()) {
      setError('Valid expiry is required.');
      return;
    }
    const expiresIso = fromHotelPickerMomentToIso(editForm.expires_at);
    if (!expiresIso) {
      setError('Valid expiry is required.');
      return;
    }
    await runUpdate(
      { expires_at: expiresIso },
      'Expiry updated'
    );
  };

  if (!open) return null;

  return (
    <>
      <Drawer
        anchor="right"
        open={open}
        onClose={onClose}
        PaperProps={{
          sx: {
            width: BOOKING_DRAWER_WIDTH_SX,
            height: '100%',
            overflow: 'hidden',
            ...getBookingDrawerPaperSx(isDark, {
              backgroundColor: isDark ? '#1e1e24' : '#f5f5f5',
            }),
          },
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', position: 'relative' }}>
          <Box
            sx={{
              ...getBookingDrawerHeaderSx(isDark),
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              p: 1.5,
              borderBottom: `2px solid ${getBookingDrawerDividerColor(isDark)}`,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
              <HowToRegIcon sx={{ color: BOOKING_DRAWER_ACCENT, fontSize: 24 }} />
              <Typography variant="h6" sx={{ color: isDark ? drawerTheme.text : '#333', fontWeight: 700 }} noWrap>
                Web Check-in
              </Typography>
              {invite ? (
                <Chip
                  size="small"
                  label={invite.status_display || invite.status}
                  sx={{
                    height: 24,
                    fontWeight: 700,
                    fontSize: '0.72rem',
                    backgroundColor: statusStyle.bg,
                    color: statusStyle.color,
                  }}
                />
              ) : !error ? (
                <Skeleton
                  variant="rounded"
                  width={78}
                  height={24}
                  animation="wave"
                  sx={{ ...skeletonSx(isDark), borderRadius: 999 }}
                />
              ) : null}
            </Box>
            <IconButton onClick={onClose} size="small" aria-label="Close web check-in drawer">
              <CloseIcon />
            </IconButton>
          </Box>

          <Box sx={{ flex: 1, overflowY: 'auto', p: 2 }}>
            {error ? (
              <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
                {error}
              </Alert>
            ) : null}

            {!invite && !error ? (
              <WebCheckInInviteDrawerSkeleton isDark={isDark} drawerTheme={drawerTheme} />
            ) : null}

            {invite ? (
              <LocalizationProvider dateAdapter={AdapterMoment}>
                <SectionPaper
                  title="Booking"
                  icon={<HotelIcon sx={{ color: BOOKING_DRAWER_ACCENT, fontSize: 18 }} />}
                  isDark={isDark}
                  drawerTheme={drawerTheme}
                  action={
                    invite.submitted_booking ? (
                      <EntityLinkButton
                        label="View booking"
                        onClick={() => {
                          const path = getBookingDetailPath(invite.submitted_booking);
                          if (!path) return;
                          onClose?.();
                          navigate(path);
                        }}
                      />
                    ) : (
                      <ViewMoreButton
                        open={expanded.booking}
                        onClick={() => setExpanded((prev) => ({ ...prev, booking: !prev.booking }))}
                      />
                    )
                  }
                >
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'stretch',
                      gap: 0,
                      minWidth: 0,
                      borderRadius: 2.5,
                      overflow: 'hidden',
                      border: `1px solid ${getBookingDrawerDividerColor(isDark)}`,
                      bgcolor: isDark ? alpha('#ffffff', 0.03) : alpha('#0f172a', 0.02),
                      boxShadow: isDark
                        ? 'none'
                        : '0 1px 2px rgba(15, 23, 42, 0.04), 0 8px 20px rgba(15, 23, 42, 0.04)',
                    }}
                  >
                    <Box
                      sx={{
                        position: 'relative',
                        width: { xs: 112, sm: 132 },
                        minHeight: { xs: 118, sm: 132 },
                        flexShrink: 0,
                        alignSelf: 'stretch',
                        overflow: 'hidden',
                        bgcolor: isDark
                          ? alpha(BOOKING_DRAWER_ACCENT, 0.12)
                          : alpha(BOOKING_DRAWER_ACCENT, 0.1),
                      }}
                    >
                      {displayRoom.image_url ? (
                        <Box
                          component="img"
                          src={displayRoom.image_url}
                          alt={
                            displayRoom.number
                              ? `Room ${displayRoom.number}`
                              : displayRoom.room_type_name || 'Room'
                          }
                          sx={{
                            width: '100%',
                            height: '100%',
                            minHeight: { xs: 118, sm: 132 },
                            objectFit: 'cover',
                            display: 'block',
                          }}
                        />
                      ) : (
                        <Box
                          sx={{
                            width: '100%',
                            height: '100%',
                            minHeight: { xs: 118, sm: 132 },
                            display: 'grid',
                            placeItems: 'center',
                            background: `linear-gradient(165deg, ${alpha(BOOKING_DRAWER_ACCENT, 0.28)} 0%, ${alpha('#0f172a', isDark ? 0.55 : 0.08)} 100%)`,
                          }}
                        >
                          <HotelIcon sx={{ fontSize: 40, color: BOOKING_DRAWER_ACCENT }} />
                        </Box>
                      )}
                      <Box
                        sx={{
                          position: 'absolute',
                          inset: 0,
                          background: `linear-gradient(180deg, ${alpha('#020617', 0.05)} 35%, ${alpha('#020617', 0.55)} 100%)`,
                          pointerEvents: 'none',
                        }}
                      />
                      {displayRoom.number ? (
                        <Chip
                          size="small"
                          label={`Room ${displayRoom.number}`}
                          sx={{
                            position: 'absolute',
                            left: 8,
                            bottom: 8,
                            height: 24,
                            maxWidth: 'calc(100% - 16px)',
                            fontWeight: 800,
                            fontSize: '0.72rem',
                            bgcolor: BOOKING_DRAWER_ACCENT,
                            color: '#031512',
                            boxShadow: '0 6px 14px rgba(0,0,0,0.28)',
                            '& .MuiChip-label': { px: 0.9 },
                          }}
                        />
                      ) : null}
                    </Box>

                    <Box
                      sx={{
                        flex: 1,
                        minWidth: 0,
                        p: { xs: 1.25, sm: 1.5 },
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 0.85,
                        justifyContent: 'center',
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1 }}>
                        <Box sx={{ minWidth: 0, flex: 1 }}>
                          <Typography
                            sx={{
                              fontWeight: 800,
                              fontSize: { xs: '0.98rem', sm: '1.05rem' },
                              letterSpacing: -0.2,
                              lineHeight: 1.25,
                              color: isDark ? drawerTheme.text : '#0f172a',
                            }}
                            noWrap
                          >
                            {displayRoom.room_type_name || (displayRoom.number ? `Room ${displayRoom.number}` : 'Room')}
                          </Typography>
                          {displayRoom.number && displayRoom.room_type_name ? (
                            <Typography
                              variant="caption"
                              sx={{
                                display: 'block',
                                mt: 0.2,
                                fontWeight: 700,
                                color: isDark ? drawerTheme.subtleText : '#64748b',
                              }}
                            >
                              Room {displayRoom.number}
                            </Typography>
                          ) : null}
                        </Box>
                        {stayRoom?.is_upgrade ? (
                          <Chip
                            size="small"
                            label="Upgrade"
                            sx={{
                              height: 22,
                              flexShrink: 0,
                              fontSize: '0.68rem',
                              fontWeight: 800,
                              bgcolor: isDark ? 'rgba(184, 134, 11, 0.2)' : 'rgba(184, 134, 11, 0.12)',
                              color: '#b8860b',
                              border: `1px solid ${isDark ? 'rgba(184, 134, 11, 0.45)' : 'rgba(184, 134, 11, 0.35)'}`,
                            }}
                          />
                        ) : null}
                        {stayRoom?.is_changed && !stayRoom?.is_upgrade ? (
                          <Chip
                            size="small"
                            label="Room Change"
                            sx={{
                              height: 22,
                              flexShrink: 0,
                              fontSize: '0.68rem',
                              fontWeight: 800,
                              bgcolor: isDark ? 'rgba(255, 152, 0, 0.2)' : 'rgba(255, 152, 0, 0.12)',
                              color: '#ff9800',
                              border: `1px solid ${isDark ? 'rgba(255, 152, 0, 0.45)' : 'rgba(255, 152, 0, 0.35)'}`,
                            }}
                          />
                        ) : null}
                      </Box>

                      <Box
                        sx={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 0.75,
                          px: 1,
                          py: 0.55,
                          width: 'fit-content',
                          maxWidth: '100%',
                          borderRadius: 1.5,
                          bgcolor: isDark ? alpha('#ffffff', 0.04) : alpha('#0f172a', 0.035),
                          border: `1px solid ${getBookingDrawerDividerColor(isDark)}`,
                        }}
                      >
                        <Typography
                          variant="caption"
                          sx={{
                            fontWeight: 700,
                            color: isDark ? drawerTheme.text : '#334155',
                            lineHeight: 1.35,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {[
                            invite.check_in_date ? formatHotelDate(invite.check_in_date) : null,
                            invite.check_out_date ? formatHotelDate(invite.check_out_date) : null,
                          ]
                            .filter(Boolean)
                            .join(' → ') || 'Stay dates unavailable'}
                          {invite.nights != null && Number(invite.nights) > 0
                            ? ` · ${invite.nights}N`
                            : ''}
                        </Typography>
                      </Box>

                      {invite.submitted_booking ? (
                        <Typography
                          variant="caption"
                          sx={{
                            fontWeight: 700,
                            color: isDark ? drawerTheme.subtleText : '#64748b',
                            letterSpacing: 0.2,
                          }}
                          noWrap
                        >
                          {invite.submitted_booking.code || `#${invite.submitted_booking.id}`}
                        </Typography>
                      ) : (
                        <Typography
                          variant="caption"
                          sx={{ fontWeight: 700, color: isDark ? drawerTheme.subtleText : '#64748b' }}
                        >
                          Not submitted
                        </Typography>
                      )}

                      {roomChangeLabel ? (
                        <Box
                          sx={{
                            px: 1,
                            py: 0.55,
                            borderRadius: 1.5,
                            bgcolor: stayRoom?.is_upgrade
                              ? (isDark ? 'rgba(184, 134, 11, 0.14)' : 'rgba(184, 134, 11, 0.08)')
                              : (isDark ? 'rgba(255, 152, 0, 0.14)' : 'rgba(255, 152, 0, 0.08)'),
                            border: `1px solid ${
                              stayRoom?.is_upgrade
                                ? (isDark ? 'rgba(184, 134, 11, 0.35)' : 'rgba(184, 134, 11, 0.28)')
                                : (isDark ? 'rgba(255, 152, 0, 0.35)' : 'rgba(255, 152, 0, 0.28)')
                            }`,
                          }}
                        >
                          <Typography
                            variant="caption"
                            sx={{
                              display: 'block',
                              fontWeight: 700,
                              color: stayRoom?.is_upgrade ? '#b8860b' : '#ff9800',
                              lineHeight: 1.35,
                            }}
                          >
                            {roomChangeLabel}
                          </Typography>
                        </Box>
                      ) : null}

                      {invite.submitted_booking ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.85, flexWrap: 'wrap', pt: 0.15 }}>
                          <Chip
                            size="small"
                            label={invite.submitted_booking.status_display || invite.submitted_booking.status}
                            sx={{
                              height: 24,
                              fontWeight: 700,
                              bgcolor: isDark ? alpha('#ffffff', 0.08) : alpha('#0f172a', 0.06),
                              color: isDark ? drawerTheme.text : '#334155',
                            }}
                          />
                          {invite.submitted_at ? (
                            <Typography
                              variant="caption"
                              sx={{ color: isDark ? drawerTheme.subtleText : '#64748b', fontWeight: 600 }}
                            >
                              Submitted {formatDateTime(invite.submitted_at)}
                            </Typography>
                          ) : null}
                        </Box>
                      ) : null}
                    </Box>
                  </Box>

                  <Collapse in={!invite.submitted_booking && expanded.booking} unmountOnExit>
                    <Box sx={{ mt: 1.25, pt: 1.25, borderTop: `1px solid ${getBookingDrawerDividerColor(isDark)}` }}>
                      {canEdit ? (
                        <Grid container spacing={1.5}>
                          <Grid item xs={12} sm={6}>
                            <HotelDatePicker
                              label="Check-in"
                              value={editForm.check_in_date ? moment(editForm.check_in_date) : null}
                              onChange={(value) =>
                                setEditForm((prev) => ({
                                  ...prev,
                                  check_in_date: value ? value.format('YYYY-MM-DD') : '',
                                }))
                              }
                              slotProps={{ textField: { size: 'small', fullWidth: true } }}
                            />
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <HotelDatePicker
                              label="Check-out"
                              value={editForm.check_out_date ? moment(editForm.check_out_date) : null}
                              onChange={(value) =>
                                setEditForm((prev) => ({
                                  ...prev,
                                  check_out_date: value ? value.format('YYYY-MM-DD') : '',
                                }))
                              }
                              slotProps={{ textField: { size: 'small', fullWidth: true } }}
                            />
                          </Grid>
                          <Grid item xs={12}>
                            <Button
                              size="small"
                              variant="contained"
                              disabled={saving}
                              onClick={handleSaveEdits}
                              sx={{ textTransform: 'none', ...primaryBtnSx }}
                            >
                              Save stay dates
                            </Button>
                          </Grid>
                        </Grid>
                      ) : (
                        <Grid container spacing={1.25}>
                          <InfoRow
                            label="Check-in"
                            value={invite.check_in_date ? formatHotelDate(invite.check_in_date) : null}
                            isDark={isDark}
                            drawerTheme={drawerTheme}
                          />
                          <InfoRow
                            label="Check-out"
                            value={invite.check_out_date ? formatHotelDate(invite.check_out_date) : null}
                            isDark={isDark}
                            drawerTheme={drawerTheme}
                          />
                        </Grid>
                      )}
                      <Grid container spacing={1.25} sx={{ mt: 0.25 }}>
                        <InfoRow
                          label="Created"
                          value={formatDateTime(invite.created_at)}
                          isDark={isDark}
                          drawerTheme={drawerTheme}
                        />
                        <InfoRow
                          label="Email sent"
                          value={invite.email_sent_at ? formatDateTime(invite.email_sent_at) : 'Not sent'}
                          isDark={isDark}
                          drawerTheme={drawerTheme}
                        />
                        <InfoRow
                          label="Submitted"
                          value={invite.submitted_at ? formatDateTime(invite.submitted_at) : '—'}
                          isDark={isDark}
                          drawerTheme={drawerTheme}
                        />
                      </Grid>
                    </Box>
                  </Collapse>
                </SectionPaper>

                <SectionPaper
                  title="Web Check-in"
                  icon={<HowToRegIcon sx={{ color: BOOKING_DRAWER_ACCENT, fontSize: 18 }} />}
                  isDark={isDark}
                  drawerTheme={drawerTheme}
                  action={
                    <Chip
                      size="small"
                      label={isActive ? 'Active' : 'Inactive'}
                      sx={{
                        height: 22,
                        fontWeight: 700,
                        fontSize: '0.7rem',
                        backgroundColor: isActive
                          ? alpha(BOOKING_DRAWER_ACCENT, 0.16)
                          : 'rgba(158, 158, 158, 0.18)',
                        color: isActive ? (isDark ? BOOKING_DRAWER_ACCENT : '#0f766e') : '#616161',
                        border: `1px solid ${
                          isActive ? alpha(BOOKING_DRAWER_ACCENT, 0.35) : 'rgba(158, 158, 158, 0.28)'
                        }`,
                      }}
                    />
                  }
                >
                  {invite.public_link ? (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'stretch',
                          gap: 0.75,
                          p: 1,
                          borderRadius: 2,
                          bgcolor: isDark
                            ? alpha(BOOKING_DRAWER_ACCENT, 0.08)
                            : alpha(BOOKING_DRAWER_ACCENT, 0.06),
                          border: `1px solid ${alpha(BOOKING_DRAWER_ACCENT, isDark ? 0.22 : 0.18)}`,
                        }}
                      >
                        <Box
                          sx={{
                            width: 34,
                            height: 34,
                            borderRadius: 1.5,
                            flexShrink: 0,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            bgcolor: alpha(BOOKING_DRAWER_ACCENT, isDark ? 0.18 : 0.14),
                            color: BOOKING_DRAWER_ACCENT,
                          }}
                        >
                          <LinkIcon sx={{ fontSize: 18 }} />
                        </Box>
                        <Box sx={{ minWidth: 0, flex: 1, py: 0.15 }}>
                          <Typography
                            variant="caption"
                            sx={{
                              display: 'block',
                              fontWeight: 700,
                              letterSpacing: 0.2,
                              color: isDark ? drawerTheme.subtleText : '#64748b',
                              mb: 0.15,
                            }}
                          >
                            Guest link
                          </Typography>
                          <Link
                            href={invite.public_link}
                            target="_blank"
                            rel="noopener noreferrer"
                            sx={{
                              display: 'block',
                              wordBreak: 'break-all',
                              fontSize: '0.78rem',
                              fontWeight: 600,
                              lineHeight: 1.35,
                              color: isDark ? BOOKING_DRAWER_ACCENT : '#0f766e',
                              textDecoration: 'none',
                              '&:hover': { textDecoration: 'underline' },
                            }}
                          >
                            {invite.public_link}
                          </Link>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25, flexShrink: 0 }}>
                          <Tooltip title="Copy link">
                            <IconButton
                              size="small"
                              onClick={handleCopyLink}
                              aria-label="Copy guest link"
                              sx={{
                                color: BOOKING_DRAWER_ACCENT,
                                bgcolor: alpha(BOOKING_DRAWER_ACCENT, isDark ? 0.12 : 0.1),
                                '&:hover': { bgcolor: alpha(BOOKING_DRAWER_ACCENT, isDark ? 0.2 : 0.16) },
                              }}
                            >
                              <ContentCopyIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Open link">
                            <IconButton
                              size="small"
                              component="a"
                              href={invite.public_link}
                              target="_blank"
                              rel="noopener noreferrer"
                              aria-label="Open guest link"
                              sx={{
                                color: BOOKING_DRAWER_ACCENT,
                                bgcolor: alpha(BOOKING_DRAWER_ACCENT, isDark ? 0.12 : 0.1),
                                '&:hover': { bgcolor: alpha(BOOKING_DRAWER_ACCENT, isDark ? 0.2 : 0.16) },
                              }}
                            >
                              <OpenInNewIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                          </Tooltip>
                          {canResend ? (
                            <Tooltip title="Resend invite">
                              <span>
                                <IconButton
                                  size="small"
                                  disabled={Boolean(saving || resending)}
                                  onClick={(event) => setResendMenuAnchor(event.currentTarget)}
                                  aria-label="Resend web check-in invite"
                                  sx={{
                                    color: BOOKING_DRAWER_ACCENT,
                                    bgcolor: alpha(BOOKING_DRAWER_ACCENT, isDark ? 0.12 : 0.1),
                                    '&:hover': { bgcolor: alpha(BOOKING_DRAWER_ACCENT, isDark ? 0.2 : 0.16) },
                                  }}
                                >
                                  {resending ? (
                                    <CircularProgress size={14} color="inherit" />
                                  ) : (
                                    <SendIcon sx={{ fontSize: 16 }} />
                                  )}
                                </IconButton>
                              </span>
                            </Tooltip>
                          ) : null}
                          {canEdit ? (
                            <Tooltip title="Regenerate link">
                              <span>
                                <IconButton
                                  size="small"
                                  disabled={saving || Boolean(resending)}
                                  onClick={() =>
                                    runUpdate({ regenerate_token: true }, 'New invite link generated')
                                  }
                                  aria-label="Regenerate guest link"
                                  sx={{
                                    color: BOOKING_DRAWER_ACCENT,
                                    bgcolor: alpha(BOOKING_DRAWER_ACCENT, isDark ? 0.12 : 0.1),
                                    '&:hover': { bgcolor: alpha(BOOKING_DRAWER_ACCENT, isDark ? 0.2 : 0.16) },
                                  }}
                                >
                                  {saving ? (
                                    <CircularProgress size={14} color="inherit" />
                                  ) : (
                                    <RefreshIcon sx={{ fontSize: 16 }} />
                                  )}
                                </IconButton>
                              </span>
                            </Tooltip>
                          ) : null}
                        </Box>
                      </Box>

                      <Menu
                        anchorEl={resendMenuAnchor}
                        open={Boolean(resendMenuAnchor)}
                        onClose={() => setResendMenuAnchor(null)}
                        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                        PaperProps={{
                          sx: {
                            mt: 0.5,
                            minWidth: 220,
                            borderRadius: 2,
                            border: `1px solid ${getBookingDrawerDividerColor(isDark)}`,
                          },
                        }}
                      >
                        <MenuItem
                          disabled={!hasGuestEmail || Boolean(resending)}
                          onClick={() => handleResend('email')}
                        >
                          <ListItemIcon>
                            {resending === 'email' ? (
                              <CircularProgress size={18} />
                            ) : (
                              <EmailIcon fontSize="small" />
                            )}
                          </ListItemIcon>
                          <ListItemText
                            primary="Resend email"
                            secondary={hasGuestEmail ? invite?.guest_email : 'Add guest email first'}
                            secondaryTypographyProps={{ noWrap: true }}
                          />
                        </MenuItem>
                        <MenuItem
                          disabled={!hasGuestPhone || Boolean(resending)}
                          onClick={() => handleResend('whatsapp')}
                        >
                          <ListItemIcon>
                            {resending === 'whatsapp' ? (
                              <CircularProgress size={18} />
                            ) : (
                              <WhatsAppIcon fontSize="small" />
                            )}
                          </ListItemIcon>
                          <ListItemText
                            primary="Resend WhatsApp"
                            secondary={
                              hasGuestPhone
                                ? formatPhoneWithCountryCode(
                                  invite?.guest_phone,
                                  invite?.guest_country_code
                                )
                                : 'Add guest phone first'
                            }
                            secondaryTypographyProps={{ noWrap: true }}
                          />
                        </MenuItem>
                        <MenuItem
                          disabled={!hasGuestEmail || !hasGuestPhone || Boolean(resending)}
                          onClick={() => handleResend('both')}
                        >
                          <ListItemIcon>
                            {resending === 'both' ? (
                              <CircularProgress size={18} />
                            ) : (
                              <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.25 }}>
                                <WhatsAppIcon fontSize="small" />
                                <EmailIcon fontSize="small" />
                              </Box>
                            )}
                          </ListItemIcon>
                          <ListItemText
                            primary="Resend both"
                            secondary={
                              hasGuestEmail && hasGuestPhone
                                ? 'Email + WhatsApp'
                                : 'Need email and phone'
                            }
                            secondaryTypographyProps={{ noWrap: true }}
                          />
                        </MenuItem>
                      </Menu>

                      <Box
                        sx={{
                          px: 1,
                          borderRadius: 2,
                          border: `1px solid ${getBookingDrawerDividerColor(isDark)}`,
                          bgcolor: isDark ? 'rgba(255,255,255,0.02)' : 'rgba(15,23,42,0.015)',
                        }}
                      >
                        <SettingRow
                          label="Link active"
                          hint={isActive ? 'Guests can open and submit' : 'Guest link is paused'}
                          isDark={isDark}
                          drawerTheme={drawerTheme}
                          control={
                            <Switch
                              checked={isActive}
                              onChange={(e) => handleToggleActive(e.target.checked)}
                              disabled={!canEdit || saving}
                              color="success"
                              size="small"
                            />
                          }
                        />
                        {canEdit ? (
                          <SettingRow
                            label="Auto check-in"
                            hint="Check guest in when they submit"
                            isDark={isDark}
                            drawerTheme={drawerTheme}
                            divider
                            control={
                              <Switch
                                checked={Boolean(invite.auto_check_in)}
                                onChange={(e) =>
                                  runUpdate(
                                    { auto_check_in: e.target.checked },
                                    e.target.checked
                                      ? 'Auto check-in enabled'
                                      : 'Auto check-in disabled'
                                  )
                                }
                                disabled={saving}
                                color="success"
                                size="small"
                              />
                            }
                          />
                        ) : (
                          <SettingRow
                            label="Auto check-in"
                            hint={invite.auto_check_in ? 'Enabled' : 'Disabled'}
                            isDark={isDark}
                            drawerTheme={drawerTheme}
                            divider
                            control={null}
                          />
                        )}
                      </Box>

                      {canEdit ? (
                        <Box
                          sx={{
                            p: 1.25,
                            borderRadius: 2,
                            border: `1px solid ${getBookingDrawerDividerColor(isDark)}`,
                            bgcolor: isDark ? 'rgba(255,255,255,0.02)' : '#fafafa',
                          }}
                        >
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                              gap: 1,
                              mb: 1,
                            }}
                          >
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
                              <ScheduleOutlinedIcon
                                sx={{ fontSize: 18, color: BOOKING_DRAWER_ACCENT, flexShrink: 0 }}
                              />
                              <Box sx={{ minWidth: 0 }}>
                                <Typography
                                  variant="body2"
                                  sx={{
                                    fontWeight: 800,
                                    color: isDark ? drawerTheme.text : '#111827',
                                    lineHeight: 1.25,
                                  }}
                                >
                                  Link expiry
                                </Typography>
                                <Typography
                                  variant="caption"
                                  sx={{
                                    color: isDark ? drawerTheme.subtleText : '#64748b',
                                    fontWeight: 600,
                                  }}
                                >
                                  {formatExpiryCountdown(invite.expires_at) || 'Set when the guest link stops working'}
                                </Typography>
                              </Box>
                            </Box>
                          </Box>

                          <Typography
                            variant="caption"
                            sx={{
                              display: 'block',
                              mb: 0.75,
                              fontWeight: 700,
                              color: isDark ? drawerTheme.subtleText : '#64748b',
                            }}
                          >
                            Quick reset from now
                          </Typography>
                          <Box sx={{ display: 'flex', gap: 0.6, flexWrap: 'wrap', mb: 1.25 }}>
                            {EXPIRY_PRESETS.map((option) => {
                              const selected = Number(invite.link_expiry_minutes) === option.value;
                              return (
                                <Chip
                                  key={option.value}
                                  size="small"
                                  label={option.label}
                                  clickable
                                  disabled={saving}
                                  onClick={() =>
                                    runUpdate(
                                      { link_expiry_minutes: option.value },
                                      `Expiry set to ${option.label}`
                                    )
                                  }
                                  sx={{
                                    height: 26,
                                    fontWeight: 800,
                                    fontSize: '0.72rem',
                                    borderRadius: '8px',
                                    border: `1px solid ${
                                      selected
                                        ? alpha(BOOKING_DRAWER_ACCENT, 0.55)
                                        : getBookingDrawerDividerColor(isDark)
                                    }`,
                                    backgroundColor: selected
                                      ? alpha(BOOKING_DRAWER_ACCENT, isDark ? 0.2 : 0.14)
                                      : 'transparent',
                                    color: selected
                                      ? (isDark ? BOOKING_DRAWER_ACCENT : '#0f766e')
                                      : (isDark ? drawerTheme.text : '#334155'),
                                  }}
                                />
                              );
                            })}
                          </Box>

                          <DateTimePicker
                            label="Expires at"
                            value={editForm.expires_at}
                            onChange={(value) => setEditForm((prev) => ({ ...prev, expires_at: value }))}
                            disabled={saving}
                            slotProps={{
                              textField: {
                                size: 'small',
                                fullWidth: true,
                                helperText: 'Uses hotel timezone',
                              },
                            }}
                          />
                          <Button
                            size="small"
                            variant="contained"
                            disabled={saving}
                            onClick={handleSaveExpiryOnly}
                            sx={{
                              mt: 1,
                              width: '100%',
                              minHeight: 36,
                              textTransform: 'none',
                              fontWeight: 800,
                              ...primaryBtnSx,
                            }}
                          >
                            Update expiry
                          </Button>
                        </Box>
                      ) : (
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            p: 1.1,
                            borderRadius: 2,
                            border: `1px solid ${getBookingDrawerDividerColor(isDark)}`,
                          }}
                        >
                          <ScheduleOutlinedIcon sx={{ fontSize: 18, color: BOOKING_DRAWER_ACCENT }} />
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: 700 }}>
                              Expires {formatDateTime(invite.expires_at)}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                              {formatExpiryCountdown(invite.expires_at)}
                            </Typography>
                          </Box>
                        </Box>
                      )}
                    </Box>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      Link unavailable
                    </Typography>
                  )}
                </SectionPaper>

                <SectionPaper
                  title="Customer"
                  icon={<PersonIcon sx={{ color: BOOKING_DRAWER_ACCENT, fontSize: 18 }} />}
                  isDark={isDark}
                  drawerTheme={drawerTheme}
                  action={
                    invite.submitted_guest?.id || invite.submitted_booking?.guest_id ? (
                      <EntityLinkButton
                        label="View customer"
                        onClick={() => {
                          const path = getCustomerDetailPath(
                            invite.submitted_guest || invite.submitted_booking?.guest_id
                          );
                          if (!path) return;
                          onClose?.();
                          navigate(path);
                        }}
                      />
                    ) : (
                      <ViewMoreButton
                        open={expanded.guest}
                        onClick={() => {
                          setExpanded((prev) => ({ ...prev, guest: !prev.guest }));
                          if (expanded.guest) setEditing(false);
                        }}
                      />
                    )
                  }
                >
                  <SummaryLine isDark={isDark} drawerTheme={drawerTheme}>
                    {invite.submitted_guest?.name || invite.guest_name || '—'}
                  </SummaryLine>
                  <MetaLine isDark={isDark} drawerTheme={drawerTheme}>
                    {[
                      invite.submitted_guest?.email || invite.guest_email,
                      formatPhoneWithCountryCode(
                        invite.submitted_guest?.phone || invite.guest_phone,
                        invite.submitted_guest?.country_code || invite.guest_country_code
                      ),
                    ]
                      .filter(Boolean)
                      .join(' · ') || 'No contact details'}
                  </MetaLine>

                  <Collapse in={!invite.submitted_booking && expanded.guest} unmountOnExit>
                    <Box sx={{ mt: 1.25, pt: 1.25, borderTop: `1px solid ${getBookingDrawerDividerColor(isDark)}` }}>
                      {canEdit ? (
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
                          <Button
                            size="small"
                            startIcon={editing ? <SaveIcon /> : <EditIcon />}
                            disabled={saving}
                            onClick={() => {
                              if (editing) {
                                handleSaveEdits();
                              } else {
                                setEditForm(buildEditState(invite));
                                setEditing(true);
                              }
                            }}
                            sx={{ textTransform: 'none', ...outlinedBtnSx }}
                          >
                            {editing ? 'Save' : 'Edit'}
                          </Button>
                        </Box>
                      ) : null}

                      {editing ? (
                        <Grid container spacing={1.5}>
                          <Grid item xs={12}>
                            <TextField
                              size="small"
                              fullWidth
                              label="Guest name"
                              value={editForm.guest_name}
                              onChange={(e) => setEditForm((prev) => ({ ...prev, guest_name: e.target.value }))}
                            />
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <TextField
                              size="small"
                              fullWidth
                              label="Email"
                              value={editForm.guest_email}
                              onChange={(e) => setEditForm((prev) => ({ ...prev, guest_email: e.target.value }))}
                            />
                          </Grid>
                          <Grid item xs={4} sm={2}>
                            <TextField
                              size="small"
                              fullWidth
                              label="Code"
                              value={editForm.guest_country_code}
                              onChange={(e) => {
                                const digits = String(e.target.value || '').replace(/\D/g, '');
                                setEditForm((prev) => ({
                                  ...prev,
                                  guest_country_code: digits ? `+${digits}` : '+',
                                }));
                              }}
                              inputProps={{ inputMode: 'tel' }}
                            />
                          </Grid>
                          <Grid item xs={8} sm={4}>
                            <TextField
                              size="small"
                              fullWidth
                              label="Phone"
                              value={editForm.guest_phone}
                              onChange={(e) => setEditForm((prev) => ({ ...prev, guest_phone: e.target.value }))}
                            />
                          </Grid>
                          <Grid item xs={12}>
                            <Box sx={{ display: 'flex', gap: 1 }}>
                              <Button
                                size="small"
                                variant="contained"
                                disabled={saving}
                                onClick={handleSaveEdits}
                                sx={{ textTransform: 'none', ...primaryBtnSx }}
                              >
                                Save changes
                              </Button>
                              <Button
                                size="small"
                                variant="text"
                                disabled={saving}
                                onClick={() => {
                                  setEditing(false);
                                  setEditForm(buildEditState(invite));
                                }}
                                sx={{ textTransform: 'none' }}
                              >
                                Cancel
                              </Button>
                            </Box>
                          </Grid>
                        </Grid>
                      ) : (
                        <Grid container spacing={1.25}>
                          <InfoRow
                            label="Email"
                            value={
                              invite.guest_email ? (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                  <EmailIcon sx={{ fontSize: 14, color: BOOKING_DRAWER_ACCENT }} />
                                  {invite.guest_email}
                                </Box>
                              ) : null
                            }
                            isDark={isDark}
                            drawerTheme={drawerTheme}
                          />
                          <InfoRow
                            label="Phone"
                            value={
                              invite.guest_phone ? (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                  <PhoneIcon sx={{ fontSize: 14, color: BOOKING_DRAWER_ACCENT }} />
                                  {formatPhoneWithCountryCode(
                                    invite.guest_phone,
                                    invite.guest_country_code
                                  )}
                                </Box>
                              ) : null
                            }
                            isDark={isDark}
                            drawerTheme={drawerTheme}
                          />
                          {Object.keys(guestSubmission).length > 0 ? (
                            <>
                              <InfoRow label="Gender" value={guestSubmission.gender} isDark={isDark} drawerTheme={drawerTheme} />
                              <InfoRow
                                label="Date of birth"
                                value={guestSubmission.dob ? formatHotelDate(guestSubmission.dob) : null}
                                isDark={isDark}
                                drawerTheme={drawerTheme}
                              />
                              <InfoRow label="Country" value={guestSubmission.country} isDark={isDark} drawerTheme={drawerTheme} />
                              <InfoRow
                                label="Purpose of stay"
                                value={
                                  guestSubmission.purpose_of_stay === 'other'
                                    ? guestSubmission.purpose_of_stay_note || 'Other'
                                    : guestSubmission.purpose_of_stay
                                }
                                isDark={isDark}
                                drawerTheme={drawerTheme}
                              />
                              <InfoRow
                                label="Document"
                                value={
                                  guestSubmission.document_type && guestSubmission.document_type !== 'no_document'
                                    ? `${guestSubmission.document_type}${guestSubmission.id_number ? ` · ${guestSubmission.id_number}` : ''}`
                                    : 'No document'
                                }
                                isDark={isDark}
                                drawerTheme={drawerTheme}
                              />
                            </>
                          ) : null}
                        </Grid>
                      )}
                    </Box>
                  </Collapse>
                </SectionPaper>

                <SectionPaper
                  title="Payment"
                  icon={<PaymentIcon sx={{ color: BOOKING_DRAWER_ACCENT, fontSize: 18 }} />}
                  isDark={isDark}
                  drawerTheme={drawerTheme}
                  action={
                    invite.submitted_payments?.length ? (
                      <EntityLinkButton
                        label="View booking"
                        onClick={() => {
                          const path = getBookingDetailPath(invite.submitted_booking);
                          if (!path) return;
                          onClose?.();
                          navigate(path);
                        }}
                      />
                    ) : invite.submitted_booking ? (
                      <EntityLinkButton
                        label="View booking"
                        onClick={() => {
                          const path = getBookingDetailPath(invite.submitted_booking);
                          if (!path) return;
                          onClose?.();
                          navigate(path);
                        }}
                      />
                    ) : (
                      <ViewMoreButton
                        open={expanded.payment}
                        onClick={() => setExpanded((prev) => ({ ...prev, payment: !prev.payment }))}
                      />
                    )
                  }
                >
                  {submittedPayments.length ? (
                    <Box>
                      <Box
                        sx={{
                          display: 'grid',
                          gridTemplateColumns: '1fr 1fr',
                          gap: 1,
                          mb: 1.25,
                          p: 1.1,
                          borderRadius: 2,
                          bgcolor: isDark
                            ? alpha(BOOKING_DRAWER_ACCENT, 0.08)
                            : alpha(BOOKING_DRAWER_ACCENT, 0.06),
                          border: `1px solid ${alpha(BOOKING_DRAWER_ACCENT, isDark ? 0.22 : 0.16)}`,
                        }}
                      >
                        <Box>
                          <Typography
                            variant="caption"
                            sx={{ color: isDark ? drawerTheme.subtleText : '#64748b', fontWeight: 700 }}
                          >
                            Booking total
                          </Typography>
                          <Typography sx={{ fontWeight: 800, fontSize: '0.92rem', color: isDark ? drawerTheme.text : '#0f172a' }}>
                            {paymentSummary.bookingTotal != null
                              ? formatMoney(paymentSummary.bookingTotal, paymentSummary.currency)
                              : '—'}
                          </Typography>
                        </Box>
                        <Box>
                          <Typography
                            variant="caption"
                            sx={{ color: isDark ? drawerTheme.subtleText : '#64748b', fontWeight: 700 }}
                          >
                            Collected
                          </Typography>
                          <Typography sx={{ fontWeight: 800, fontSize: '0.92rem', color: BOOKING_DRAWER_ACCENT }}>
                            {formatMoney(paymentSummary.collected, paymentSummary.currency)}
                          </Typography>
                        </Box>
                        <Box>
                          <Typography
                            variant="caption"
                            sx={{ color: isDark ? drawerTheme.subtleText : '#64748b', fontWeight: 700 }}
                          >
                            Web check-in due
                          </Typography>
                          <Typography sx={{ fontWeight: 700, fontSize: '0.86rem', color: isDark ? drawerTheme.text : '#1f2937' }}>
                            {paymentSummary.requiredAmount != null
                              ? formatMoney(paymentSummary.requiredAmount, paymentSummary.currency)
                              : invite.payment_requirement_display || '—'}
                          </Typography>
                        </Box>
                        <Box>
                          <Typography
                            variant="caption"
                            sx={{ color: isDark ? drawerTheme.subtleText : '#64748b', fontWeight: 700 }}
                          >
                            Remaining
                          </Typography>
                          <Typography sx={{ fontWeight: 700, fontSize: '0.86rem', color: isDark ? drawerTheme.text : '#1f2937' }}>
                            {paymentSummary.remaining != null
                              ? formatMoney(paymentSummary.remaining, paymentSummary.currency)
                              : '—'}
                          </Typography>
                        </Box>
                      </Box>

                      <Typography
                        variant="caption"
                        sx={{
                          display: 'block',
                          mb: 0.85,
                          fontWeight: 800,
                          letterSpacing: 0.4,
                          textTransform: 'uppercase',
                          color: isDark ? drawerTheme.subtleText : '#64748b',
                        }}
                      >
                        {paymentSummary.paymentCount} payment
                        {paymentSummary.paymentCount === 1 ? '' : 's'}
                      </Typography>

                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.85 }}>
                        {submittedPayments.map((payment, index) => {
                          const method =
                            payment.payment_method_display
                            || payment.payment_method
                            || 'Payment';
                          const statusLabel = String(payment.status || 'unknown');
                          const statusKey = statusLabel.toLowerCase();
                          const statusTone = statusKey === 'success'
                            ? { bg: 'rgba(76, 175, 80, 0.14)', color: '#2e7d32' }
                            : statusKey === 'failed'
                              ? { bg: 'rgba(244, 67, 54, 0.14)', color: '#c62828' }
                              : statusKey === 'pending'
                                ? { bg: 'rgba(255, 152, 0, 0.14)', color: '#e65100' }
                                : { bg: 'rgba(158, 158, 158, 0.16)', color: '#616161' };
                          const typeLabel = payment.payment_type
                            ? String(payment.payment_type).replace(/_/g, ' ')
                            : '';
                          return (
                            <Box
                              key={payment.id || `${payment.provider_txn_id || 'pay'}-${index}`}
                              sx={{
                                p: 1.1,
                                borderRadius: 2,
                                border: `1px solid ${getBookingDrawerDividerColor(isDark)}`,
                                bgcolor: isDark ? 'rgba(255,255,255,0.02)' : 'rgba(15,23,42,0.015)',
                              }}
                            >
                              <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1 }}>
                                <Box sx={{ minWidth: 0, flex: 1 }}>
                                  <Typography
                                    sx={{
                                      fontWeight: 800,
                                      fontSize: '0.9rem',
                                      color: isDark ? drawerTheme.text : '#0f172a',
                                      lineHeight: 1.3,
                                    }}
                                  >
                                    {formatMoney(payment.amount, payment.currency || paymentSummary.currency)}
                                    <Typography
                                      component="span"
                                      sx={{
                                        ml: 0.75,
                                        fontWeight: 600,
                                        fontSize: '0.8rem',
                                        color: isDark ? drawerTheme.subtleText : '#64748b',
                                      }}
                                    >
                                      · {method}
                                    </Typography>
                                  </Typography>
                                  <Typography
                                    variant="caption"
                                    sx={{
                                      display: 'block',
                                      mt: 0.35,
                                      color: isDark ? drawerTheme.subtleText : '#64748b',
                                      fontWeight: 600,
                                    }}
                                  >
                                    {[
                                      typeLabel ? typeLabel.replace(/\b\w/g, (c) => c.toUpperCase()) : null,
                                      payment.processed_at ? formatDateTime(payment.processed_at) : null,
                                      payment.provider ? String(payment.provider) : null,
                                    ].filter(Boolean).join(' · ') || 'Payment record'}
                                  </Typography>
                                  {payment.provider_txn_id ? (
                                    <Typography
                                      variant="caption"
                                      sx={{
                                        display: 'block',
                                        mt: 0.25,
                                        color: isDark ? drawerTheme.subtleText : '#94a3b8',
                                        fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                                        wordBreak: 'break-all',
                                      }}
                                    >
                                      Txn {payment.provider_txn_id}
                                    </Typography>
                                  ) : null}
                                </Box>
                                <Chip
                                  size="small"
                                  label={statusLabel}
                                  sx={{
                                    height: 22,
                                    textTransform: 'capitalize',
                                    fontWeight: 700,
                                    fontSize: '0.68rem',
                                    bgcolor: statusTone.bg,
                                    color: statusTone.color,
                                    flexShrink: 0,
                                  }}
                                />
                              </Box>
                            </Box>
                          );
                        })}
                      </Box>

                      <MetaLine isDark={isDark} drawerTheme={drawerTheme}>
                        {invite.payment_requirement_display || invite.payment_requirement || 'Payment'}
                        {' · '}
                        {invite.payment_status_display || invite.payment_status || '—'}
                      </MetaLine>
                    </Box>
                  ) : (
                    <>
                      <SummaryLine isDark={isDark} drawerTheme={drawerTheme}>
                        {`${invite.payment_requirement_display || invite.payment_requirement || '—'} · ${
                          invite.payment_status_display || invite.payment_status || '—'
                        }`}
                      </SummaryLine>
                      <MetaLine isDark={isDark} drawerTheme={drawerTheme}>
                        {invite.payment_requirement === 'none'
                          ? 'No payment required'
                          : invite.payment_requirement === 'pay_at_hotel'
                            ? `Pay at hotel ${formatMoney(invite.booking_total_amount, invite.payment_currency)}`
                            : `Amount ${formatMoney(invite.payment_amount, invite.payment_currency)}`}
                      </MetaLine>

                      <Collapse in={!invite.submitted_booking && expanded.payment} unmountOnExit>
                        <Box sx={{ mt: 1.25, pt: 1.25, borderTop: `1px solid ${getBookingDrawerDividerColor(isDark)}` }}>
                          <Grid container spacing={1.25}>
                            <InfoRow
                              label="Booking total"
                              value={formatMoney(invite.booking_total_amount, invite.payment_currency)}
                              isDark={isDark}
                              drawerTheme={drawerTheme}
                            />
                            <InfoRow
                              label="Payment amount"
                              value={formatMoney(invite.payment_amount, invite.payment_currency)}
                              isDark={isDark}
                              drawerTheme={drawerTheme}
                            />
                          </Grid>
                        </Box>
                      </Collapse>
                    </>
                  )}
                </SectionPaper>
              </LocalizationProvider>
            ) : null}
          </Box>

          {invite && canCancel ? (
            <Box
              sx={{
                flexShrink: 0,
                p: 1.5,
                ...getBookingDrawerFooterSx(isDark),
              }}
            >
              <Button
                fullWidth
                variant="outlined"
                color="error"
                disabled={saving}
                startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <CancelOutlinedIcon />}
                onClick={() => setCancelConfirmOpen(true)}
                sx={{
                  minHeight: 44,
                  textTransform: 'none',
                  fontWeight: 800,
                  borderColor: isDark ? 'rgba(239, 83, 80, 0.55)' : 'rgba(211, 47, 47, 0.45)',
                  color: isDark ? '#ef9a9a' : '#c62828',
                  '&:hover': {
                    borderColor: isDark ? '#ef5350' : '#c62828',
                    backgroundColor: isDark ? 'rgba(239, 83, 80, 0.12)' : 'rgba(211, 47, 47, 0.06)',
                  },
                }}
              >
                Cancel Web Check-in
              </Button>
            </Box>
          ) : null}

          {cancelConfirmOpen ? (
            <Box
              sx={{
                position: 'absolute',
                inset: 0,
                zIndex: 20,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                p: 2,
                backgroundColor: isDark ? 'rgba(0,0,0,0.72)' : 'rgba(0,0,0,0.45)',
              }}
              onClick={() => {
                if (!saving) setCancelConfirmOpen(false);
              }}
            >
              <Paper
                elevation={0}
                onClick={(e) => e.stopPropagation()}
                sx={{
                  width: '100%',
                  maxWidth: 360,
                  p: 2.25,
                  borderRadius: 2.5,
                  bgcolor: isDark ? '#25252d' : '#fff',
                  color: isDark ? drawerTheme.text : '#333',
                  border: `1px solid ${getBookingDrawerDividerColor(isDark)}`,
                  boxShadow: isDark
                    ? '0 16px 40px rgba(0,0,0,0.55)'
                    : '0 12px 28px rgba(0,0,0,0.18)',
                }}
              >
                <Typography variant="h6" sx={{ fontWeight: 800, fontSize: '1.05rem', mb: 1 }}>
                  Cancel web check-in?
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ color: isDark ? drawerTheme.subtleText : '#616161', lineHeight: 1.5, mb: 2.25 }}
                >
                  This cancels the invite for{' '}
                  <Box component="span" sx={{ fontWeight: 700, color: isDark ? drawerTheme.text : '#333' }}>
                    {invite?.guest_name || 'this guest'}
                  </Box>
                  {invite?.room?.number || invite?.room_number
                    ? ` · Room ${invite?.room?.number || invite?.room_number}`
                    : ''}
                  . The guest link stops working and the room can be booked again.
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
                  <Button
                    onClick={() => setCancelConfirmOpen(false)}
                    disabled={saving}
                    sx={{
                      textTransform: 'none',
                      fontWeight: 700,
                      color: isDark ? BOOKING_DRAWER_ACCENT : '#2e7d32',
                    }}
                  >
                    Keep invite
                  </Button>
                  <Button
                    variant="contained"
                    color="error"
                    disabled={saving}
                    onClick={handleCancelRequest}
                    startIcon={saving ? <CircularProgress size={14} color="inherit" /> : <CancelOutlinedIcon />}
                    sx={{ textTransform: 'none', fontWeight: 800 }}
                  >
                    Cancel request
                  </Button>
                </Box>
              </Paper>
            </Box>
          ) : null}
        </Box>
      </Drawer>

      <Snackbar
        open={copyOpen}
        autoHideDuration={2200}
        onClose={() => setCopyOpen(false)}
        message={snackMessage}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </>
  );
};

export default WebCheckInInviteDrawer;
